import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ImageObject, ListS3, S3Item } from 'types';
import { ProductAvailable } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';
import { ProductColor } from 'types/api/product.type';

import { priceTypeStore } from 'lib/priceTypeStore';

import { useAppSelector } from 'redux/hooks';

import { ordersDataService } from 'services';

import { BucketContext } from 'contexts/BucketContext';

import { buildProductTitle } from 'helpers/buildProductTitle';
import { filterThumbs } from 'helpers/filterThumbs';
import { getSearchParams } from 'helpers/getSearchParams';
import {
  getAvailablePriceTypes,
  getDefaultPriceType,
  getInitialPriceType,
} from 'helpers/pricing';

type SingleProductProps = {
  children: React.ReactNode;
};

export type SingleProduct = {
  product?: ProductColor;
  productSet: Dispatch<SetStateAction<ProductColor | undefined>>;
  productOrder?: ProductAvailable;
  image: string;
  title: string;
  galleryUrls: ImageObject[];
  isGalleryVisible: boolean;
  isProductModalVisible: boolean;
  isOrderModalVisible: boolean;
  isSelectionModalVisible: boolean;
  isGalleryVisibleSet: (arg: boolean) => void;
  isProductModalVisibleSet: (arg: boolean) => void;
  isOrderModalVisibleSet: (arg: boolean) => void;
  isSelectionModalVisibleSet: (arg: boolean) => void;
  productOrderSet: Dispatch<SetStateAction<ProductAvailable>>;
  selectedPriceType: PriceType | undefined;
  selectedPriceTypeSet: Dispatch<SetStateAction<PriceType | undefined>>;
  availablePriceTypes: PriceType[];
};

const defaultState: SingleProduct = {
  product: undefined,
  productSet: () => null,
  productOrder: undefined,
  image: '',
  title: '',
  galleryUrls: [],
  isGalleryVisible: false,
  isProductModalVisible: false,
  isOrderModalVisible: false,
  isSelectionModalVisible: false,
  isGalleryVisibleSet: () => null,
  isProductModalVisibleSet: () => null,
  isOrderModalVisibleSet: () => null,
  isSelectionModalVisibleSet: () => null,
  productOrderSet: () => null,
  selectedPriceType: undefined,
  selectedPriceTypeSet: () => null,
  availablePriceTypes: [],
};

export const SingleProductContext = createContext(defaultState);

export const SingleProductProvider: React.FunctionComponent<SingleProductProps> = ({
  children,
}) => {
  const { id } = useParams();
  const { getListItems, getUrl } = useContext(BucketContext);
  const [searchParams] = useSearchParams();
  const searchParamsData = useMemo(() => getSearchParams(searchParams), [searchParams]);
  const { season, orderId } = useAppSelector((state) => state.settings);
  const [product, productSet] = useState<ProductColor>();
  const [image, imageSet] = useState<string>('');
  const [title, titleSet] = useState<string>('');
  const [isGalleryVisible, isGalleryVisibleSet] = useState<boolean>(false);
  const [isProductModalVisible, isProductModalVisibleSet] = useState<boolean>(false);
  const [isOrderModalVisible, isOrderModalVisibleSet] = useState<boolean>(false);
  const [isSelectionModalVisible, isSelectionModalVisibleSet] = useState<boolean>(false);
  const [galleryUrls, galleryUrlsSet] = useState<ImageObject[]>([]);
  const [selectedPriceType, selectedPriceTypeSet] = useState<PriceType | undefined>();
  const [productOrder, productOrderSet] = useState<ProductAvailable>({
    available: false,
    quantities: [],
  });

  const availablePriceTypes = useMemo(
    () => getAvailablePriceTypes(product?.sizeQtyPrice.map((s) => s.resolvedPrices) ?? []),
    [product]
  );

  // Auto-select the remembered (or default) price type when product data arrives or changes
  useEffect(() => {
    if (product?.sizeQtyPrice && product.sizeQtyPrice.length > 0) {
      const firstWithPrices = product.sizeQtyPrice.find(
        (s) => s.resolvedPrices && s.resolvedPrices.length > 0
      );
      const defaultType = getDefaultPriceType(firstWithPrices?.resolvedPrices);
      selectedPriceTypeSet(
        getInitialPriceType(
          priceTypeStore.getPriceType(),
          getAvailablePriceTypes(product.sizeQtyPrice.map((s) => s.resolvedPrices)),
          defaultType
        )
      );
    } else {
      selectedPriceTypeSet(undefined);
    }
  }, [product]);

  const state: SingleProduct = useMemo(
    () => ({
      isGalleryVisible,
      isProductModalVisible,
      isOrderModalVisible,
      isSelectionModalVisible,
      isGalleryVisibleSet,
      isProductModalVisibleSet,
      isOrderModalVisibleSet,
      isSelectionModalVisibleSet,
      productOrderSet,
      product,
      productSet,
      galleryUrls,
      image,
      productOrder,
      title,
      selectedPriceType,
      selectedPriceTypeSet,
      availablePriceTypes,
    }),
    [
      product,
      galleryUrls,
      isGalleryVisible,
      isProductModalVisible,
      isOrderModalVisible,
      isSelectionModalVisible,
      image,
      productOrder,
      title,
      selectedPriceType,
      availablePriceTypes,
    ]
  );

  useEffect(() => {
    if (product && season) {
      const code = product?.imageName.slice(6, product?.imageName.length - 4); // eslint-disable-line
      getListItems(`catalogue/products/${season}/product_gallery/${code}/`).then(
        (res: ListS3) => {
          const imageKeys = (res.Contents ?? [])
            .map((item) => item.Key)
            .filter((key) => !key.endsWith('/'));

          if (imageKeys.length > 0) {
            Promise.all(imageKeys.map((key) => getUrl(key))).then((url) => {
              if (url) {
                const urlIntoObject = filterThumbs(url)?.map(
                  (item, index): ImageObject => ({
                    id: index,
                    key: item.key,
                    url: item.url,
                  })
                );
                galleryUrlsSet(urlIntoObject);
              }
            });
          }
        }
      );
    }
  }, [product, season]);

  useEffect(() => {
    if (product) {
      getUrl(`catalogue/products${product.imageName}`).then((res: S3Item) => {
        imageSet(res.url);
      });
    }
  }, [product]);

  useEffect(() => {
    if (orderId && searchParamsData) {
      const { itemCode, mainFabricCode, colorCode, itemColorCode } = searchParamsData;

      const productCode = itemCode ?? '';
      const fabric = mainFabricCode ?? '';
      const color = itemColorCode ?? colorCode ?? '';

      if (productCode && color) {
        ordersDataService
          .checkProductStatus(orderId, productCode, color, fabric || undefined)
          .then((res) => {
            const { data } = res;
            if (data.inOrder) {
              productOrderSet({
                available: true,
                quantities: [],
                itemId: data.itemId,
                sizes: data.sizes,
              });
            } else {
              productOrderSet({ available: false, quantities: [] });
            }
          })
          .catch(() => {
            productOrderSet({ available: false, quantities: [] });
          });
      } else {
        productOrderSet({ available: false, quantities: [] });
      }
    } else {
      productOrderSet({ available: false, quantities: [] });
    }
  }, [orderId, id, searchParams]);

  useEffect(() => {
    if (Object.keys(searchParamsData).length > 0) {
      titleSet(buildProductTitle(searchParamsData));
    }
  }, [searchParamsData]);

  return (
    <SingleProductContext.Provider value={state}>{children}</SingleProductContext.Provider>
  );
};
